import companyCreatePageStyles from './company-create-page.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../../components/agwa-page-header';
import { router, resolveRouterPath } from '../../router';
import { terms } from '../../language/terms';
import './company-create-form.js';
import type { CompanyCreatedDetail } from './company-create-form.js';

/**
 * Route host for creating a company at `maintenance/companies/new`.
 *
 * A page, not a dialog, on the architect's ruling of 2026-09-04 (D-089): the form carries too
 * much to fit a dialog well, and the repository's routes-vs-dialogs rule already says complex
 * forms get routed pages. Per the portability rules the page does almost nothing - it mounts
 * the form and turns its intent events into navigation.
 */
@customElement('company-create-page')
export class CompanyCreatePage extends LitElement {
  static styles = unsafeCSS(companyCreatePageStyles);

  /**
   * Where a successful save lands: the new company's own workspace, and nowhere else.
   *
   * ONE DESTINATION BY RULING (D-128, architect 2026-09-18), not a simplification anyone should
   * undo. This page used to have a second - the site-creation workspace, reached when a tickbox on
   * the form was ticked - and that shortcut is what the decision removed: a create lands on the
   * thing it just created, and sites are added from that workspace after staff. So the detail no
   * longer carries a navigation instruction and there is nothing here to branch on.
   *
   * A save whose response carried no group falls back to the list, which still shows the row - a
   * workspace route built on an absent id would be a page that cannot load.
   */
  private handleCreated(e: CustomEvent<CompanyCreatedDetail>) {
    const groupId = e.detail.group?.id;
    if (!groupId) {
      void router?.navigate(resolveRouterPath('maintenance/companies'));
      return;
    }
    void router?.navigate(
      resolveRouterPath(`maintenance/companies/${groupId}`)
    );
  }

  /**
   * Cancel goes to the companies list - named for its destination, matching the workspace's
   * ruling that a fixed-destination control must not promise "back".
   */
  private handleCancelled() {
    void router?.navigate(resolveRouterPath('maintenance/companies'));
  }

  render() {
    return html`
      <div class="create-page">
        <!-- Companies > New Company (the trail standard, architect 2026-09-06). -->
        <agwa-page-header
          .crumbs=${[
            {
              label: terms.loginGroup.plural,
              href: resolveRouterPath('maintenance/companies'),
            },
          ]}
        >
          New ${terms.loginGroup.value}
        </agwa-page-header>
        <company-create-form
          @company-created=${this.handleCreated}
          @create-cancelled=${this.handleCancelled}
        ></company-create-form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'company-create-page': CompanyCreatePage;
  }
}
